import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "react-query";
import {
  createNomination,
  deleteNomination,
  getListNomination,
  updateNomination,
} from "../../api/nomination";
import { isNumeric } from "../../utils";

const defaultNomination = {
  name: "",
  cost: "",
  therapist_amount: "",
  color: "#ffffff",
  background: "#111fdc",
  is_main: false,
  is_free: false,
  remarks: "",
};

const defaultNominationErrors = {
  name: "",
  cost: "",
  therapist_amount: "",
  color: "",
  background: "",
  is_main: false,
  is_free: false,
  remarks: "",
};

const useNominationMaster = () => {
  const [arrNominations, setArrNominations] = useState([]);
  const [activeSortColumn, setActiveSortColumn] = useState(0);
  const [sortColumnDirection, setSortColumnDirection] = useState("desc");
  const [sortedData, setSortedData] = useState([]);
  const [newNomination, setNewNomination] = useState(defaultNomination);

  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);

  const [editNomination, setEditNomination] = useState(defaultNomination);
  const [isPopupOpenDelete, setIsPopupOpenDelete] = useState(false);
  const [isPopupOpenUpdate, setIsPopupOpenUpdate] = useState(false);
  const [nominationToDelete, setNominationToDelete] = useState(null);
  const [focusState, setFocusState] = useState({
    name: false,
    cost: false,
    therapist_amount: false,
  });

  const nameInputRef = useRef(null);

  const [errors, setErrors] = useState(defaultNominationErrors);

  const [errorsUpdate, setErrorsUpdate] = useState(defaultNominationErrors);

  const { data: nominationsData, refetch: refetchNominations } = useQuery(
    ["nominations", page, rowsPerPage],
    () => {
      const params = {
        page: page + 1,
        per_page: rowsPerPage,
      };
      return getListNomination(params);
    },
    {
      keepPreviousData: true,
    }
  );

  useEffect(() => {
    if (nominationsData) {
      setArrNominations(transformNominationData(nominationsData.data.data));
      setTotal(nominationsData.data.total);
    }
  }, [nominationsData]);

  useEffect(() => {
    setSortedData(arrNominations.sort((a, b) => b.id - a.id));
  }, [arrNominations]);

  useEffect(() => {
    if (nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, []);

  const transformNominationData = (data) => {
    return data.map((nomination) => ({
      id: nomination.id.toString(),
      name: nomination.name,
      cost: parseInt(nomination.cost),
      therapist_amount: nomination.therapist_amount !== null ? parseInt(nomination.therapist_amount) : null,
      color: nomination.color,
      background: nomination.background,
      is_main: nomination.is_main ? "〇" : "×",
      is_free: nomination.is_free ? "〇" : "×",
      remarks: nomination.remarks || "",
    }));
  };

  const handleSort = (columnIndex) => {
    const isSorted = activeSortColumn === columnIndex;
    let direction = isSorted
      ? sortColumnDirection === "asc"
        ? "desc"
        : "asc"
      : "desc";
    sortedData.sort((a, b) => {
      let aValue = a[Object.keys(a)[columnIndex]];
      let bValue = b[Object.keys(b)[columnIndex]];

      // if aValue and bValue are numberic, sort parseInt
      if (isNumeric(aValue) && isNumeric(bValue)) {
        aValue = parseInt(aValue);
        bValue = parseInt(bValue);
      }

      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });
    setSortedData([...sortedData]);
    setActiveSortColumn(columnIndex);
    setSortColumnDirection(direction);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewNomination({ ...newNomination, [name]: value });
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleCheckboxNewBackground = (e) => {
    const { checked } = e.target;
    setNewNomination((prev) => ({
      ...prev,
      background: checked ? "#111fdc" : null,
    }));
  }

  const handleAddNomination = async () => {
    try {
      const newNominationData = {
        name: newNomination.name,
        cost: newNomination.cost,
        therapist_amount: newNomination.therapist_amount,
        is_main: newNomination.is_main,
        is_free: newNomination.is_free,
        remarks: newNomination.remarks,
        color: newNomination.color,
        background: newNomination.background,
      };

      const response = await createNomination(newNominationData);

      if (response && response.data) {
        refetchNominations();
        setNewNomination(defaultNomination);
        setErrors(defaultNominationErrors);
      } else {
        alert("エラーが発生しました。");
      }
    } catch (error) {
      if (error.response && error.response.status === 422) {
        const fieldErrors = error.response.data.message;
        setErrors((prev) => ({
          ...prev,
          ...fieldErrors,
        }));
      }
      else {
        alert("エラーが発生しました。");
      }
    }
  };

  const handleUpdateNomination = async (updatedNomination) => {
    try {
      const { id, name, cost, is_main, is_free, therapist_amount, color, background, remarks } = updatedNomination;
      const updatedData = {
        name: name,
        cost: cost,
        is_main: is_main,
        is_free: is_free,
        therapist_amount: therapist_amount,
        remarks: remarks,
        color: color,
        background: background,
      };
      const response = await updateNomination(id, updatedData);

      if (response && response.data) {
        refetchNominations();
        return true
      } else {
        alert("エラーが発生しました。");
        return false
      }
    } catch (error) {
      if (error.response && error.response.status === 422) {
        const fieldErrors = error.response.data.message;
        setErrorsUpdate((prev) => ({
          ...prev,
          ...fieldErrors,
        }));
      }
      else {
        alert("エラーが発生しました。");
      }
      return false
    }
  };

  const handleDeleteNomination = async (id) => {
    try {
      const response = await deleteNomination(id);

      if (response.success) {
        refetchNominations();
      } else {
        alert("エラーが発生しました。");
      }
    } catch (error) {
      alert("エラーが発生しました。");
    }
  };

  const handleToggleIsMain = (e) => {
    const isChecked = e.target.checked;
    setNewNomination({ ...newNomination, is_main: isChecked });
  };

  const handleToggleIsFree = (e) => {
    const isChecked = e.target.checked;
    setNewNomination({ ...newNomination, is_free: isChecked });
  };

  const handleToggleChange = (e) => {
    const isChecked = e.target.checked;

    if (!isChecked) {
      setNewNomination({ ...newNomination, therapist_amount: null });
    } else {
      setNewNomination({ ...newNomination, therapist_amount: "" })
    }
  };

  const handleCheckboxEditBackground = (e) => {
    const { checked } = e.target;
    setEditNomination((prev) => ({
      ...prev,
      background: checked ? "#111fdc" : null,
    }));
  }

  const handleRowsPerPageChange = (event) => {
    const newRowsPerPage = parseInt(event.target.value);
    const maxPage = Math.floor(total / newRowsPerPage);
    setRowsPerPage(newRowsPerPage);
    setPage(Math.min(page, maxPage));
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleFocus = (field) => {
    setFocusState((prevState) => ({
      ...prevState,
      [field]: true,
    }));
  };

  const handleBlur = (field) => {
    setFocusState((prevState) => ({
      ...prevState,
      [field]: false,
    }));
  };

  const handleClosePopup = () => {
    setIsPopupOpenUpdate(false);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setEditNomination((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    setErrorsUpdate((prev) => ({ ...prev, [name]: "" }));
  };

  const handleUpdateToggleIsMain = (e) => {
    const isChecked = e.target.checked;
    setEditNomination({ ...editNomination, is_main: isChecked });
  }

  const handleUpdateToggleIsFree = (e) => {
    const isChecked = e.target.checked;
    setEditNomination({ ...editNomination, is_free: isChecked });
  }

  const handleToggle = useCallback((e) => {
    const isChecked = e.target.checked;
    if (!isChecked) {
      setEditNomination({ ...editNomination, therapist_amount: null });
    } else {
      setEditNomination({ ...editNomination, therapist_amount: "" });
    }
  }, [editNomination]);

  const handleEditClick = (nomination) => {
    setEditNomination({
      ...nomination,
      is_main: nomination.is_main === "〇",
      is_free: nomination.is_free === "〇",
    });
    setIsPopupOpenUpdate(true);
  };

  const handleDeleteClick = (nomination) => {
    setNominationToDelete(nomination);
    setIsPopupOpenDelete(true);
  };

  return {
    arrNominations,
    activeSortColumn,
    sortColumnDirection,
    sortedData,
    newNomination,
    rowsPerPage,
    page,
    total,
    editNomination,
    isPopupOpenDelete,
    isPopupOpenUpdate,
    nominationToDelete,
    focusState,
    nameInputRef,
    errorsUpdate,
    errors,
    handleRowsPerPageChange,
    handlePageChange,
    handleSort,
    handleInputChange,
    handleAddNomination,
    handleUpdateNomination,
    handleDeleteNomination,
    handleToggleChange,
    handleToggle,
    handleFocus,
    handleBlur,
    handleClosePopup,
    handleChange,
    handleEditClick,
    handleDeleteClick,
    setIsPopupOpenDelete,
    handleCheckboxNewBackground,
    handleCheckboxEditBackground,
    handleToggleIsMain,
    handleUpdateToggleIsMain,
    handleToggleIsFree,
    handleUpdateToggleIsFree,
  };
};

export default useNominationMaster;
